import React, { useState, useCallback } from 'react';
import {
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Slider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  Switch,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Close as CloseIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { useSearch } from '@/contexts/SearchContext';
import { SearchFilters } from '@/contexts/SearchContext';
import { useSearchFilters } from '@/hooks/api/usePropertySearch';

interface FilterBarProps {
  onFiltersChange?: (filters: SearchFilters) => void;
  showResultsCount?: boolean;
  resultsCount?: number;
}

const FilterBar: React.FC<FilterBarProps> = ({
  onFiltersChange,
  showResultsCount = true,
  resultsCount = 0,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { 
    state, 
    setActiveFilters, 
    setFilterModalOpen, 
    hasActiveFilters, 
    getActiveFilterCount 
  } = useSearch();
  
  const { data: availableFilters } = useSearchFilters();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState<SearchFilters>(state.activeFilters);

  // Handle opening filter modal
  const handleOpenModal = () => {
    setTempFilters(state.activeFilters);
    setIsModalOpen(true);
    setFilterModalOpen(true);
  };

  // Handle closing filter modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFilterModalOpen(false);
  };

  // Handle applying filters
  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    if (onFiltersChange) {
      onFiltersChange(tempFilters);
    }
    handleCloseModal();
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    const clearedFilters: SearchFilters = {
      priceRange: { min: 0, max: 1000 },
      propertyTypes: [],
      amenities: [],
      instantBook: undefined,
    };
    setTempFilters(clearedFilters);
    setActiveFilters(clearedFilters);
    if (onFiltersChange) {
      onFiltersChange(clearedFilters);
    }
  };

  // Handle removing individual filter
  const handleRemoveFilter = (filterType: string, value?: string) => {
    const newFilters = { ...state.activeFilters };
    
    switch (filterType) {
      case 'priceRange':
        newFilters.priceRange = { min: 0, max: 1000 };
        break;
      case 'propertyType':
        if (value) {
          newFilters.propertyTypes = newFilters.propertyTypes.filter(type => type !== value);
        }
        break;
      case 'amenity':
        if (value) {
          newFilters.amenities = newFilters.amenities.filter(amenity => amenity !== value);
        }
        break;
      case 'instantBook':
        newFilters.instantBook = undefined;
        break;
    }
    
    setActiveFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  // Handle price range change
  const handlePriceRangeChange = useCallback((event: Event, newValue: number | number[]) => {
    const [min, max] = newValue as number[];
    setTempFilters(prev => ({
      ...prev,
      priceRange: { min, max },
    }));
  }, []);

  // Handle property type change
  const handlePropertyTypeChange = (type: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      propertyTypes: checked
        ? [...prev.propertyTypes, type]
        : prev.propertyTypes.filter(t => t !== type),
    }));
  };

  // Handle amenity change
  const handleAmenityChange = (amenity: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      amenities: checked
        ? [...prev.amenities, amenity]
        : prev.amenities.filter(a => a !== amenity),
    }));
  };

  // Handle instant book change
  const handleInstantBookChange = (checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      instantBook: checked ? true : undefined,
    }));
  };

  // Get active filter chips
  const getActiveFilterChips = () => {
    const chips = [];
    const { activeFilters } = state;

    // Price range chip
    if (activeFilters.priceRange.min > 0 || activeFilters.priceRange.max < 1000) {
      chips.push({
        key: 'priceRange',
        label: `$${activeFilters.priceRange.min} - $${activeFilters.priceRange.max}`,
        onDelete: () => handleRemoveFilter('priceRange'),
      });
    }

    // Property type chips
    activeFilters.propertyTypes.forEach(type => {
      chips.push({
        key: `propertyType-${type}`,
        label: type,
        onDelete: () => handleRemoveFilter('propertyType', type),
      });
    });

    // Amenity chips
    activeFilters.amenities.forEach(amenity => {
      chips.push({
        key: `amenity-${amenity}`,
        label: amenity,
        onDelete: () => handleRemoveFilter('amenity', amenity),
      });
    });

    // Instant book chip
    if (activeFilters.instantBook) {
      chips.push({
        key: 'instantBook',
        label: 'Instant Book',
        onDelete: () => handleRemoveFilter('instantBook'),
      });
    }

    return chips;
  };

  const activeFilterChips = getActiveFilterChips();
  const activeFilterCount = getActiveFilterCount();

  return (
    <>
      {/* Filter Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2,
          px: isMobile ? 2 : 0,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        {/* Results Count */}
        {showResultsCount && (
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
            {resultsCount} {resultsCount === 1 ? 'property' : 'properties'}
          </Typography>
        )}

        {/* Filter Button */}
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={handleOpenModal}
          sx={{
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 'fit-content',
            backgroundColor: activeFilterCount > 0 ? 'primary.main' : 'transparent',
            color: activeFilterCount > 0 ? 'primary.contrastText' : 'text.primary',
            borderColor: activeFilterCount > 0 ? 'primary.main' : 'divider',
            '&:hover': {
              backgroundColor: activeFilterCount > 0 ? 'primary.dark' : 'action.hover',
              borderColor: activeFilterCount > 0 ? 'primary.dark' : 'divider',
            },
          }}
        >
          Filters
          {activeFilterCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: activeFilterCount > 0 ? 'primary.contrastText' : 'primary.main',
                color: activeFilterCount > 0 ? 'primary.main' : 'primary.contrastText',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {activeFilterCount}
            </Box>
          )}
        </Button>

        {/* Active Filter Chips */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            overflow: 'auto',
            flex: 1,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
          }}
        >
          {activeFilterChips.map(chip => (
            <Chip
              key={chip.key}
              label={chip.label}
              onDelete={chip.onDelete}
              size="small"
              sx={{
                borderRadius: 2,
                backgroundColor: 'grey.100',
                '& .MuiChip-deleteIcon': {
                  fontSize: '1rem',
                },
              }}
            />
          ))}
        </Box>

        {/* Clear All Button */}
        {hasActiveFilters() && (
          <Button
            variant="text"
            size="small"
            onClick={handleClearFilters}
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              minWidth: 'fit-content',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            Clear all
          </Button>
        )}
      </Box>

      {/* Filter Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            maxHeight: isMobile ? '100vh' : '80vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Filters
          </Typography>
          <IconButton onClick={handleCloseModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3 }}>
          {/* Price Range */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Price range (per night)
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={[tempFilters.priceRange.min, tempFilters.priceRange.max]}
                onChange={handlePriceRangeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `$${value}`}
                min={0}
                max={1000}
                step={10}
                marks={[
                  { value: 0, label: '$0' },
                  { value: 250, label: '$250' },
                  { value: 500, label: '$500' },
                  { value: 750, label: '$750' },
                  { value: 1000, label: '$1000+' },
                ]}
                sx={{
                  '& .MuiSlider-thumb': {
                    backgroundColor: 'primary.main',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                ${tempFilters.priceRange.min}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ${tempFilters.priceRange.max === 1000 ? '1000+' : tempFilters.priceRange.max}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Property Types */}
          {availableFilters?.propertyTypes && availableFilters.propertyTypes.length > 0 && (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Property type
                </Typography>
                <FormGroup>
                  <Grid container spacing={1}>
                    {availableFilters.propertyTypes.map(type => (
                      <Grid item xs={6} key={type}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={tempFilters.propertyTypes.includes(type)}
                              onChange={(e) => handlePropertyTypeChange(type, e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {type}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Box>

              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Amenities */}
          {availableFilters?.amenities && availableFilters.amenities.length > 0 && (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Amenities
                </Typography>
                <FormGroup>
                  <Grid container spacing={1}>
                    {availableFilters.amenities.slice(0, 12).map(amenity => (
                      <Grid item xs={6} key={amenity}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={tempFilters.amenities.includes(amenity)}
                              onChange={(e) => handleAmenityChange(amenity, e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography variant="body2">
                              {amenity}
                            </Typography>
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Box>

              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Instant Book */}
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={tempFilters.instantBook === true}
                  onChange={(e) => handleInstantBookChange(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Instant Book
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Book without waiting for host approval
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              flex: 1,
            }}
          >
            Clear all
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              flex: 2,
            }}
          >
            Show {resultsCount} {resultsCount === 1 ? 'property' : 'properties'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FilterBar;